import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, CheckCircle, Building2, GraduationCap } from 'lucide-react';
import CommonContactUs from './CommonContactUs';

const TalentAcquisitionPage = () => {
  return (
    <div className="w-full min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-[#1A193B] pt-20 text-white py-20 px-4 md:px-6">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-3 lg:px-4 py-24 lg:py-32">
        <div className="container mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
              Talent Acquisition
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-6">
              Find the best tech talent for your organization
            </p>
            <p className="text-lg text-blue-200">
              Our Services
            </p>
          </motion.div>
        </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 md:py-16 px-4 md:px-6 bg-white">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex items-center gap-4 mb-6"
            >
              <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-gradient-to-r from-blue-500 to-cyan-500 flex-shrink-0">
                <Users className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800">
                Talent Acquisition Services
              </h2>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-lg text-gray-600 mb-8"
            >
              Our comprehensive talent acquisition services help organizations find, attract, and retain the best tech talent. We combine industry expertise, advanced matching algorithms, and personalized service to deliver exceptional results.
            </motion.p>

            {/* How We Help Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-gray-50 p-6 rounded-xl mb-10"
            >
              <h3 className="text-xl font-semibold text-gray-800 mb-4">
                How We Help You Find the Perfect Talent
              </h3>
              <div className="space-y-4">
                <div className="flex gap-3">
                  <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-800">Executive Search</h4>
                    <p className="text-gray-600">
                      We identify and recruit top-tier executives and leaders who can drive your organization forward. Our executive search process is thorough, discreet, and focused on finding candidates who align with your company's vision and culture.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-800">Technical Screening</h4>
                    <p className="text-gray-600">
                      Our rigorous technical screening process ensures that candidates have the skills and expertise they claim. We use a combination of assessments, interviews, and practical tests to evaluate technical proficiency.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-800">Culture Fit Assessment</h4>
                    <p className="text-gray-600">
                      Beyond technical skills, we assess how well candidates will integrate with your team and company culture. This reduces turnover and enhances team cohesion and productivity.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-800">Onboarding Support</h4>
                    <p className="text-gray-600">
                      We don't just help you find talent; we support the entire onboarding process to ensure a smooth transition for both the employer and the new hire.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Process Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <h3 className="text-xl font-semibold text-gray-800 mb-4">
                Our Talent Acquisition Process
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                <div className="bg-white p-5 rounded-xl shadow-md">
                  <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold mb-3">
                    1
                  </div>
                  <h4 className="font-semibold text-gray-800 mb-2">Understand Your Needs</h4>
                  <p className="text-gray-600">
                    We start by deeply understanding your requirements, company culture, and the specific skills and attributes you're looking for.
                  </p>
                </div>
                <div className="bg-white p-5 rounded-xl shadow-md">
                  <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold mb-3">
                    2
                  </div>
                  <h4 className="font-semibold text-gray-800 mb-2">Source & Screen</h4>
                  <p className="text-gray-600">
                    We leverage our extensive network and advanced tools to identify potential candidates and conduct initial screenings.
                  </p>
                </div>
                <div className="bg-white p-5 rounded-xl shadow-md">
                  <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold mb-3">
                    3
                  </div>
                  <h4 className="font-semibold text-gray-800 mb-2">Present & Place</h4>
                  <p className="text-gray-600">
                    We present you with a shortlist of qualified candidates and support you through the interview, selection, and onboarding processes.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="flex justify-center gap-4 flex-wrap"
            >
              <Link to="/services">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-gray-100 text-gray-800 hover:bg-gray-200 px-6 py-3 rounded-lg font-medium transition-colors shadow-md inline-flex items-center"
                >
                  <span>Back to Services</span>
                </motion.button>
              </Link>
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-6 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg shadow-lg flex items-center space-x-2 transition-colors duration-300"
                >
                  <Users size={18} />
                  <span>Contact Us</span>
                </motion.button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
      

      {/* Contact Form Section */}
      {/* <section id="talent-acquisition-contact" className="py-12 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="bg-white p-8 rounded-xl shadow-lg"
            >
              <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
                Get Started with Talent Acquisition
              </h2>
              <p className="text-gray-600 mb-8 text-center">
                Ready to find the perfect talent for your organization? Contact us today to discuss your needs.
              </p>
              
              <form className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                      placeholder="Your company name"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Contact Person *
                    </label>
                    <input
                      type="text"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                      placeholder="Your name"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                      placeholder="email@company.com"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                      placeholder="+1 (234) 567-8910"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Position Requirements *
                  </label>
                  <textarea
                    required
                    rows="4"
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                    placeholder="Describe the position you're looking to fill, including required skills, experience level, and any specific requirements..."
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Additional Information
                  </label>
                  <textarea
                    rows="3"
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                    placeholder="Any additional information about your company, timeline, or specific needs..."
                  />
                </div>

                <div className="text-center">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    className="px-8 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors shadow-lg"
                  >
                    Request Talent Acquisition Services
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </section> */}
       <section className="py-16 bg-[#1A193B] text-white">
        <CommonContactUs/>
      </section>
    </div>
  );
};

export default TalentAcquisitionPage; 